import {gql} from 'graphql-request';
import {BillingStatusType, CancelReason} from '@/enums';
import {
  ExecuteOnetimePaymentRecord, ExecutePayment, IssueBillingKey, IssueBillingKeyRecord,
  PaymentHistory, PaymentProduct, PaymentsTranscation
} from '@/interface';
import {QueryFunctionContext, useMutation, useQuery} from '@tanstack/react-query';
import {useGqlRequest} from './request';

export const useFindProduct = (userId?: string) => {
  const request = useGqlRequest({
    query: FIND_PRODUCT,
  });

  const queryFn = (
    context: QueryFunctionContext
  ):
    | undefined
    | Promise<{
        result?: PaymentProduct[];
      }> => {
    return request(context);
  };

  const select = (data?: {result?: PaymentProduct[]}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['payments/find-product', userId || 'guest'],
    select,
  });
};

export const useCreateTransaction = () => {
  const request = useGqlRequest({
    query: CREATE_TRANSCATION,
  });

  const mutationFn = (record: {
    customerKey: string;
    method: string;
    product: string;
  }): Promise<{
    result?: PaymentsTranscation;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useIssueBillingKey = () => {
  const request = useGqlRequest({
    query: ISSUE_BILLING_KEY,
  });

  const mutationFn = (
    record: IssueBillingKeyRecord
  ): Promise<{
    result?: IssueBillingKey;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useExecutePayment = () => {
  const request = useGqlRequest({
    query: EXECUTE_PAYMENT,
  });

  const mutationFn = (record: {
    customerKey: string;
    transactionId: string;
  }): Promise<{
    result?: ExecutePayment;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useExecuteOnetimePayment = () => {
  const request = useGqlRequest({
    query: EXECUTE_ONETIME_PAYMENT,
  });

  const mutationFn = (
    record: ExecuteOnetimePaymentRecord
  ): Promise<{
    result?: ExecutePayment;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useExecuteTrial = () => {
  const request = useGqlRequest({
    query: EXECUTE_TRIAL,
  });

  const mutationFn = (record: {
    customerKey: string;
    transactionId: string;
  }): Promise<{
    result?: ExecutePayment;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useChangeCardInfo = () => {
  const request = useGqlRequest({
    query: CHANGE_CARD_INFO,
  });

  const mutationFn = (
    record: IssueBillingKeyRecord
  ): Promise<{
    result?: IssueBillingKey;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useChangeSubscription = () => {
  const request = useGqlRequest({
    query: CHANGE_SUBSCRIPTION,
  });

  const mutationFn = (record: {
    state: BillingStatusType;
    reason?: CancelReason;
    other?: string;
  }): Promise<{
    result?: boolean;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const usePaymentHistory = (userId?: string) => {
  const request = useGqlRequest({
    query: PAYMENT_HISTORY,
  });

  const queryFn = (
    context: QueryFunctionContext
  ):
    | undefined
    | Promise<{
        result?: PaymentHistory[];
      }> => {
    return request(context);
  };

  const select = (data?: {result?: PaymentHistory[]}) => data?.result;

  return useQuery({
    queryFn,
    enabled: !!userId?.length,
    queryKey: ['payments/history', userId],
    select,
  });
};

const FIND_PRODUCT = gql`
  query findProduct {
    result: findProduct {
      amount
      billingType
      discount
      id
      isActive
      metadata
      name
      paymentType
      tier
      type
      seq
      badge
      value
    }
  }
`;

const CREATE_TRANSCATION = gql`
  mutation createTransaction(
    $customerKey: String!
    $method: String!
    $product: ID!
  ) {
    result: createTransaction(
      customerKey: $customerKey
      method: $method
      product: $product
    ) {
      customerKey
      id
      status
      type
    }
  }
`;

const ISSUE_BILLING_KEY = gql`
  mutation issueBillingKey(
    $authKey: String!
    $transactionId: ID!
    $customerKey: String!
  ) {
    result: issueBillingKey(
      authKey: $authKey
      transactionId: $transactionId
      customerKey: $customerKey
    ) {
      authenticatedAt
      customerKey
      transactionId
    }
  }
`;

const EXECUTE_PAYMENT = gql`
  mutation executePayment($customerKey: String!, $transactionId: ID!) {
    result: executePayment(
      customerKey: $customerKey
      transactionId: $transactionId
    ) {
      tier
      useBeganAt
      useEndedAt
    }
  }
`;

const EXECUTE_ONETIME_PAYMENT = gql`
  mutation executePaymentConfirm(
    $amount: Float!
    $customerKey: String!
    $paymentKey: String!
    $transactionId: ID!
  ) {
    result: executePaymentConfirm(
      amount: $amount
      customerKey: $customerKey
      paymentKey: $paymentKey
      transactionId: $transactionId
    ) {
      tier
      useBeganAt
      useEndedAt
    }
  }
`;

const EXECUTE_TRIAL = gql`
  mutation executeTrial($customerKey: String!, $transactionId: ID!) {
    result: executeTrial(
      customerKey: $customerKey
      transactionId: $transactionId
    ) {
      tier
      useBeganAt
      useEndedAt
    }
  }
`;

const CHANGE_CARD_INFO = gql`
  mutation changeCardInfo(
    $authKey: String!
    $customerKey: String!
    $transactionId: ID!
  ) {
    result: changeCardInfo(
      authKey: $authKey
      customerKey: $customerKey
      transactionId: $transactionId
    ) {
      authenticatedAt
      customerKey
      transactionId
    }
  }
`;

const CHANGE_SUBSCRIPTION = gql`
  mutation changeSubscription(
    $state: BillingStatusType!
    $reason: CancelReason
    $other: String
  ) {
    result: changeSubscription(state: $state, reason: $reason, other: $other)
  }
`;

const PAYMENT_HISTORY = gql`
  query paymentHistory {
    result: paymentHistory {
      method
      orderName
      receiptUrl
      status
      totalAmount
      suppliedAmount
      vat
      authenticatedAt
      cardCompany
      cardNumber
    }
  }
`;

